/**
 * Hook da curva forward Zeus (Task #50 Fase B).
 *
 * Padrao do useLastSync: cache module-level + refetch periodico.
 * TTL 10min (curva muda menos que sync, mais que calendario CCEE).
 *
 * Fallback gracioso em erro de rede/auth/5xx → array vazio + flag de erro.
 */
import { useEffect, useState } from "react";
import { getCurvaForward } from "../api/client";
import type { CurvaForwardRow } from "../api/types";
import { startVisiblePolling } from "./visiblePolling";

const REFETCH_MS = 10 * 60 * 1000;

interface CacheEntry {
  expiresAt: number;
  promise: Promise<CurvaForwardRow[]>;
}

let cache: CacheEntry | null = null;

export function invalidateCurvaForwardCache(): void {
  cache = null;
}

export interface UseCurvaForwardState {
  data: CurvaForwardRow[];
  isLoading: boolean;
  error: Error | null;
}

export function useCurvaForward(): UseCurvaForwardState {
  const [state, setState] = useState<UseCurvaForwardState>({
    data: [],
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let cancel = false;

    function load() {
      const now = Date.now();
      if (!cache || cache.expiresAt < now) {
        cache = {
          expiresAt: now + REFETCH_MS,
          promise: getCurvaForward().catch((err: Error) => {
            // Devolve [] mas reanuncia o erro pelo state pra UI mostrar empty
            // state com aviso. Diferente do useLastSync que tem fallback de
            // payload sintetico — aqui [] eh suficiente.
            throw err;
          }),
        };
      }
      cache.promise
        .then((data) => {
          if (cancel) return;
          setState({ data, isLoading: false, error: null });
        })
        .catch((err: Error) => {
          if (cancel) return;
          setState({ data: [], isLoading: false, error: err });
        });
    }

    load();
    // #75: tick skipa com a aba em background; volta visivel/focus -> 1 load.
    const stop = startVisiblePolling(load, REFETCH_MS);
    return () => {
      cancel = true;
      stop();
    };
  }, []);

  return state;
}
